// local definition of the network.connection interface
interface connection {
  downlink: number;
  downlinkMax: number;
  effectiveType: string;
  rtt: number;
  saveData: boolean;
  type: string;
}

/**
 * Checks if the current browser has access to the NetworkInformation API
 * by checking if the object exists
 * @param networkInfo the current instance of window.network.connection, i.e. NetworkInformation
 * @returns bool describing if the NetworkInformation/Connection.RTT API is usable
 */
export const checkIsRttCompatible = (networkInfo: connection) => {
  return networkInfo != null && networkInfo.rtt != null; //if "connection" is null, it means it's not supported on the current browser
};

/**
 * Checks if the current browser has access to the Performance API
 * by checking if the object exists
 * @returns bool describing if the Performance API is usable
 */
export const checkIsPerformanceCompatible = () => {
  return getLatencyByPerformance(10) > 0; //if result is -1, it means it's not supported on the current browser
};

/**
 * uses window.navigator.connection's networkInfo data to get current latency (experimental as of 1/2022)
 * doesn't work for safari, IE, firefox as of Jan 2022, but may be more reliable than performance estimate
 * https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 * @param networkInfo the current instance of window.network.connection, i.e. NetworkInformation
 * @returns current rtt (response round-trip time), i.e. latency, in MS
 * */

export const getLatencyByNetInfo = (networkInfo: connection) => {
  return networkInfo.rtt;
};

/**
 * use resourceTiming's performance data to estimate current latency (cross-platform)
 * https://developer.mozilla.org/en-US/docs/Web/API/Resource_Timing_API/Using_the_Resource_Timing_API
 * @param sizeToTest
 * @returns estimated rount-trip time, i.e. latency, in MS
 */
export const getLatencyByPerformance = (sizeToTest: number) => {
  if (sizeToTest == null) {
    sizeToTest = 10;
  }
  // performance contains network events and associated stats regarding them
  if (performance != null) {
    //script events response times are not representative of current latency, so ignore them
    //@ts-ignore
    const applicableEntries = performance.getEntriesByType("resource").filter((x) => x.initiatorType != "script");
    if (applicableEntries.length > 1) {
      var avgRecentResponseTime = 0;
      let numChecked = 0;
      if (applicableEntries.length < sizeToTest) {
        sizeToTest = applicableEntries.length;
      }
      for (let i = applicableEntries.length - sizeToTest; i < applicableEntries.length; i++) {
        if (applicableEntries[i].duration != 0) {
          avgRecentResponseTime += applicableEntries[i].duration;
          numChecked++;
        }
      }
      return (avgRecentResponseTime = avgRecentResponseTime / numChecked);
    }
  }

  return -1;
};

//#endregion
